import { useStore } from '@nanostores/react'
import { mapping } from 'actions/mapping'
import clsx from 'clsx'
import { CustomTip } from 'components/atoms/tooltipCustom'
import { campaignApi } from 'features/campaigns'
import { executionsUtils } from 'features/executions'
import { type CSSProperties, Fragment, useMemo } from 'react'
import { useUnMount } from 'shared/hooks'
import { initHoverActions } from 'shared/position/hover-actions'
import { ContactsTable, StackIcon } from 'shared/ui'

import type {
	ActionWithId,
	CampaignExecutionState,
	ExecutionHumanState,
	StepExecution
} from '../../types'

import css from './style.module.scss'

const { $id, popoverListeners, triggerListeners, useSyncPopoverState } =
	initHoverActions({ anchorCN: css.currentAnchor })
interface InfoProps {
	campaignId: string
	humanState: ExecutionHumanState
	state: CampaignExecutionState
	steps: StepExecution[]
}

let cache: Record<string, InfoProps> = {}

export function Info({
	campaignId,
	enabled,
	hint,
	humanState,
	id,
	state,
	steps
}: ActionWithId & {
	campaignId: string
	humanState: ExecutionHumanState
	state: CampaignExecutionState
	steps: StepExecution[]
}) {
	const actions = useMemo(
		() => steps.filter(step => step.type !== 'Delay'),
		[steps]
	)

	return (
		<CustomTip arrow title={hint}>
			<button
				className={ContactsTable.css.cellAction}
				disabled={!enabled}
				{...triggerListeners}
				onClick={event => {
					cache[id] = {
						campaignId,
						humanState,
						state,
						steps: actions
					}
					triggerListeners.onClick(event)
				}}
				type='button'
				value={id}
			>
				<StackIcon icon='info' width={18} />
			</button>
		</CustomTip>
	)
}

export function InfoPopup() {
	const ref = useSyncPopoverState()

	useUnMount(() => {
		cache = {}
	})

	return (
		<div
			className={`${css.menu} app-scroll-thin`}
			{...popoverListeners}
			ref={ref}
		>
			<Content />
		</div>
	)
}

function Content() {
	const id = useStore($id)
	const data = cache[id]

	const { data: campaign } = campaignApi.useGetCampaignsByIdQuery(data?.campaignId ?? '', {
		skip: !data?.campaignId
	})

	if (!data) {
		return null
	}

	const { humanState, state, steps } = data

	const executionTitle = executionsUtils.getExecutionTitle(state, campaign?.steps)

	return (
		<>
			<h5 className={css.title}>{executionTitle}</h5>
			<dl className={css.list}>
				{steps.map((item, index) => (
					<Fragment key={index}>
						<div className={css.item}>
							<dt className={css.action}>Action</dt>

							<dd className={css.value}>{mapping.activeName(item.type)}</dd>
						</div>

						<div className={clsx(css.right, css.item)}>
							<dt className={css.label}>Status</dt>

							<dd
								className={clsx(css.value, css.color)}
							style={
								{
									'--active': executionsUtils.colorByStep(humanState, item)
								} as CSSProperties
							}
						>
							{' '}
							{executionsUtils.getStepTitle(item.human_state!, item.type!)}
							</dd>
						</div>
					</Fragment>
				))}
			</dl>
		</>
	)
}
